import { Check, Minus, X } from 'lucide-react';
import React, { useMemo } from 'react';

import { cn } from '@/lib/utils';

import { STAGE_CONFIG } from './stage-config';
import type { MiniTortTimelineEntry } from './types';
import { MiniTortStage, STAGE_ORDER } from './types';

/** Stages that are optional detours — not required in the happy path */
const DETOUR_STAGES = new Set([
  MiniTortStage.NEED_ADDITIONAL_MATERIALS,
  MiniTortStage.RETRIEVING_POLICE_REPORT,
]);

interface MiniTortStageStepperProps {
  currentStage: MiniTortStage;
  /** Timeline entries for extracting completion timestamps and visited stages */
  timeline?: MiniTortTimelineEntry[];
  className?: string;
}

/** Extract the timestamp when a stage was completed (transitioned away from). */
function buildStageTimestamps(timeline: MiniTortTimelineEntry[] | undefined): Map<MiniTortStage, string> {
  const map = new Map<MiniTortStage, string>();
  if (!timeline) return map;

  timeline.forEach((entry) => {
    if (entry.event_type === 'stage_changed' && entry.old_stage && !map.has(entry.old_stage)) {
      map.set(entry.old_stage, entry.timestamp);
    }
  });

  return map;
}

/** Determine which stages were actually visited (entered as new_stage in timeline). */
function buildVisitedStages(timeline: MiniTortTimelineEntry[] | undefined): Set<MiniTortStage> {
  const visited = new Set<MiniTortStage>();
  if (!timeline) return visited;

  timeline.forEach((entry) => {
    if (entry.event_type === 'stage_changed' && entry.new_stage) {
      visited.add(entry.new_stage);
    }
  });

  return visited;
}

function formatStepTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export const MiniTortStageStepper: React.FC<MiniTortStageStepperProps> = ({
  currentStage,
  timeline,
  className,
}) => {
  const isRejected = currentStage === MiniTortStage.REJECTED;
  const currentIndex = STAGE_ORDER.indexOf(currentStage);
  const stageTimestamps = useMemo(() => buildStageTimestamps(timeline), [timeline]);
  const visitedStages = useMemo(() => buildVisitedStages(timeline), [timeline]);

  return (
    <div className={cn('w-full', className)}>
      {isRejected ? (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 dark:border-red-800 dark:bg-red-900/20">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/50">
            <X className="h-4 w-4 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-red-700 dark:text-red-300">Claim Rejected</p>
            <p className="text-xs text-red-600 dark:text-red-400">This claim has been closed as ineligible</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {STAGE_ORDER.map((stage, index) => {
            const config = STAGE_CONFIG[stage];
            const isActive = stage === currentStage;
            const isCompleted = currentIndex > index;
            const completedAt = stageTimestamps.get(stage);
            const isDetour = DETOUR_STAGES.has(stage);

            // A detour stage that was never visited and is now behind the current stage = skipped
            const wasSkipped = isDetour && !isActive && isCompleted && !visitedStages.has(stage);

            return (
              <React.Fragment key={stage}>
                {index > 0 && (
                  <div
                    className={cn(
                      'h-0.5 flex-1',
                      isCompleted && wasSkipped && 'bg-zinc-300 dark:bg-zinc-600',
                      isCompleted && !wasSkipped && 'bg-emerald-400 dark:bg-emerald-600',
                      !isCompleted && 'bg-zinc-200 dark:bg-zinc-700',
                    )}
                  />
                )}
                <div className="flex flex-col items-center" title={wasSkipped ? `${config.label} (skipped)` : config.label}>
                  <div
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-all',
                      wasSkipped && 'bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-500',
                      !wasSkipped && isCompleted && 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
                      isActive && 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-500/30 dark:bg-indigo-900/50 dark:text-indigo-300',
                      !isCompleted && !isActive && !wasSkipped && 'bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-500',
                    )}
                  >
                    {wasSkipped && <Minus className="h-3.5 w-3.5" />}
                    {!wasSkipped && isCompleted && <Check className="h-3.5 w-3.5" />}
                    {!wasSkipped && !isCompleted && <span>{index + 1}</span>}
                  </div>
                  <span
                    className={cn(
                      'mt-1 max-w-[70px] text-center text-[9px] font-medium leading-tight',
                      wasSkipped && 'text-zinc-400 dark:text-zinc-500',
                      isActive && !wasSkipped && 'text-indigo-700 dark:text-indigo-300',
                      !isActive && !wasSkipped && 'text-zinc-500 dark:text-zinc-400',
                    )}
                  >
                    {wasSkipped ? 'Skipped' : config.shortLabel}
                  </span>
                  {/* Completion timestamp for completed (non-skipped) stages */}
                  {isCompleted && !wasSkipped && completedAt && (
                    <span className="mt-0.5 text-center text-[8px] font-medium text-zinc-400 dark:text-zinc-500">
                      {formatStepTimestamp(completedAt)}
                    </span>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};
